import ShopFnbFacilityHeader from '@/components/common/shop-fnb-facility/header';
import ToggleTypeAccordionList from '@/components/feature/toggle-type/toggle-type-accordion-list';
import ToggleTypeCategoryBar from '@/components/feature/toggle-type/toggle-type-category-bar';
import { BuildingMenuType } from '@/enums';
import { useOrientation } from '@/hooks/useOrientation';
import { useConfigStore } from '@/stores';
import { TOGGLE_TYPE_ALL_CATEGORY_ID } from '@/types/toggle-type';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './ToggleTypePage.module.scss';
import { getToggleTypeItemsByCategory } from '@/utils/custom-menu';
import ShopFnbFacilityCarouselEmpty from '@/components/common/shop-fnb-facility/carousel-empty';
import { useTranslation } from 'react-i18next';
import { useCategoryList, useCustomMenuContent } from '@/hooks/custom-menu';

const ToggleTypePage = () => {
    const { t } = useTranslation();

    const ui = useConfigStore((state) => state.config.ui);
    const server = useConfigStore((state) => state.config.server);
    const { isHorizontal } = useOrientation();
    const { gnbMenuId } = useParams<{ gnbMenuId?: string }>();

    const customContent = useCustomMenuContent(gnbMenuId);
    const categories = useCategoryList(gnbMenuId);

    const [selectedCategoryId, setSelectedCategoryId] = useState(TOGGLE_TYPE_ALL_CATEGORY_ID);

    const categoryOrder = useMemo(
        () => categories.filter((category) => category.id !== TOGGLE_TYPE_ALL_CATEGORY_ID).map((category) => category.id),
        [categories]
    );

    const currentMenu = useMemo(() => {
        const listMenus = server?.buildingMenus?.[ui.language] || [];
        if (gnbMenuId) {
            return listMenus.find((menu) => menu.id === gnbMenuId && menu.menuType === BuildingMenuType.CUSTOM_MENU);
        }
        return listMenus.find((menu) => menu.menuType === BuildingMenuType.CUSTOM_MENU);
    }, [server?.buildingMenus, ui.language, gnbMenuId]);

    const filteredItems = useMemo(
        () => getToggleTypeItemsByCategory(selectedCategoryId, customContent, categoryOrder),
        [selectedCategoryId, customContent, categoryOrder]
    );

    const handleCategorySelect = useCallback((id: string) => {
        setSelectedCategoryId(id);
    }, []);

    const pageClassname = useMemo(
        () =>
            clsx(styles['toggle-type'], {
                [styles['toggle-type--vertical']]: !isHorizontal,
                [styles['toggle-type--horizontal']]: isHorizontal,
                [styles['toggle-type--contrast']]: ui.contrast,
                [styles['toggle-type--horizontal--halfmode']]: ui.screenMode === 'split' && isHorizontal,
                [styles['toggle-type--vertical--halfmode']]: ui.screenMode === 'split' && !isHorizontal,
            }),
        [ui.screenMode, ui.contrast, isHorizontal]
    );

    const headerClassname = useMemo(
        () =>
            clsx(styles['toggle-type__header'], {
                [styles['toggle-type__header--horizontal']]: isHorizontal,
                [styles['toggle-type__header--vertical']]: !isHorizontal,
                [styles['toggle-type__header--vertical--halfmode']]: !isHorizontal && ui.screenMode === 'split',
            }),
        [isHorizontal, ui.screenMode]
    );

    return (
        <div className={pageClassname}>
            <div className={headerClassname}>
                <ShopFnbFacilityHeader
                    title={currentMenu?.name as string}
                    description={currentMenu?.description as string}
                    isHorizontal={isHorizontal}
                    tabIndex={1}
                />
            </div>

            {
                filteredItems.length === 0 ? <ShopFnbFacilityCarouselEmpty
                        id="list-type-empty-result"
                        contrast={ui.contrast}
                        halfMode={ui.screenMode === 'split'}
                        isHorizontal={isHorizontal}
                        tabIndex={2}
                        textEmpty={t('custom_menu.empty')}
                        ariaLabel={t('custom_menu.empty')}
                    /> : <>
            <div className={styles['toggle-type__list']}>
                <ToggleTypeAccordionList items={filteredItems} isHorizontal={isHorizontal} />
            </div>

            <ToggleTypeCategoryBar
                categories={categories}
                selectedCategoryId={selectedCategoryId}
                onSelect={handleCategorySelect}
                isHorizontal={isHorizontal}
            /></>
            }
        </div>
    );
};

export default ToggleTypePage;
